import type { ApiEnvelope, ApiErrorBody } from '@greco-gls/shared';
import { ERROR_CODES } from '@greco-gls/shared';
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
} from 'axios';

/**
 * The one HTTP client (spec §9.1, port of MGA's `api/client.ts`).
 *
 * Three things it exists to do:
 *  - put the bearer token on every request without every caller thinking about it;
 *  - unwrap the `{data}` envelope so screens deal in payloads, not envelopes;
 *  - turn a failure into an `ApiClientError` carrying the backend's error **code**, because the UI
 *    branches on codes (`REVISION_CONFLICT`, `USER_NOT_REGISTERED`, …) and must never parse
 *    messages.
 */

export interface ApiClientOptions {
  baseUrl: string;
  /** Called before each request; null means "no token available, send none". */
  getAccessToken?: () => Promise<string | null>;
  /** Local development without MSAL: the backend accepts `X-Dev-Bypass` when ENV=development. */
  devBypass?: boolean;
}

const UNKNOWN_ERROR = 'UNKNOWN_ERROR';
/** Axios stopped waiting (`timeout` below). Says nothing about what the server did (ADR 0007). */
const REQUEST_TIMEOUT = 'REQUEST_TIMEOUT';

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Authenticated, but not registered in `app_users` — the app shows the no-access page. */
  get isNotRegistered(): boolean {
    return this.code === ERROR_CODES.userNotRegistered;
  }

  /** Somebody saved the same cell first (spec §7.6). */
  get isRevisionConflict(): boolean {
    return this.code === ERROR_CODES.revisionConflict;
  }

  /** The client gave up waiting; the server may still finish what was asked (ADR 0007). */
  get isTimeout(): boolean {
    return this.code === REQUEST_TIMEOUT;
  }

  /** Field errors from `400 VALIDATION_ERROR`, in the shape react-hook-form can consume. */
  get fieldErrors(): Record<string, string> {
    const details = this.details as { fields?: { path?: string; message?: string }[] } | undefined;
    const fields = details?.fields ?? [];
    const mapped: Record<string, string> = {};
    for (const field of fields) {
      if (!field.path) continue;
      // `body.name` -> `name`: the form knows nothing about where the value travelled.
      mapped[field.path.replace(/^body\./, '')] = field.message ?? 'Invalid value';
    }
    return mapped;
  }
}

function toClientError(error: unknown): ApiClientError {
  if (error instanceof ApiClientError) return error;

  const axiosError = error as AxiosError<ApiErrorBody>;
  if (axiosError?.isAxiosError) {
    const body = axiosError.response?.data;
    if (body && typeof body === 'object' && 'error' in body && body.error) {
      return new ApiClientError(
        axiosError.response?.status ?? 0,
        body.error.code,
        body.error.message,
        body.error.details,
      );
    }
    if (axiosError.code === 'ECONNABORTED' || axiosError.code === 'ETIMEDOUT') {
      // Axios stopped waiting. The server may still be working on the request and commit it — a
      // two-minute import apply did exactly that while the user saw an error (ADR 0007).
      return new ApiClientError(
        0,
        REQUEST_TIMEOUT,
        'The server did not answer in time. It may still be working on the request.',
      );
    }
    if (!axiosError.response) {
      // No response at all: the API is down, or CORS refused the request.
      return new ApiClientError(0, 'NETWORK_ERROR', 'Could not reach the Glossa API.');
    }
    return new ApiClientError(
      axiosError.response.status,
      UNKNOWN_ERROR,
      axiosError.message || 'Request failed.',
    );
  }

  return new ApiClientError(0, UNKNOWN_ERROR, error instanceof Error ? error.message : 'Request failed.');
}

let instance: AxiosInstance | null = null;
let options: ApiClientOptions | null = null;

export function configureApiClient(next: ApiClientOptions): void {
  options = next;
  instance = axios.create({
    baseURL: next.baseUrl.replace(/\/$/, ''),
    // Past this the request fails with `REQUEST_TIMEOUT`. A caller whose server work can outlive
    // the wait (an import apply) treats that as "ask after it", not as a failure (ADR 0007).
    timeout: 60_000,
    headers: { Accept: 'application/json' },
  });

  instance.interceptors.request.use(async (config) => {
    if (options?.devBypass) {
      config.headers.set('X-Dev-Bypass', 'true');
      return config;
    }
    const token = await options?.getAccessToken?.();
    if (token) config.headers.set('Authorization', `Bearer ${token}`);
    return config;
  });
}

function client(): AxiosInstance {
  if (!instance) {
    throw new Error('configureApiClient() must run before the first request (see main.tsx)');
  }
  return instance;
}

/** Response of a list endpoint: the items plus the `meta` the envelope carried. */
export interface PagedResult<T> {
  items: T[];
  total: number | null;
  cursor: string | null;
}

async function request<T>(config: AxiosRequestConfig): Promise<ApiEnvelope<T>> {
  try {
    const response = await client().request<ApiEnvelope<T>>(config);
    return response.data;
  } catch (error) {
    throw toClientError(error);
  }
}

export async function apiGet<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  return (await request<T>({ method: 'GET', url, params })).data;
}

export async function apiGetPaged<T>(
  url: string,
  params?: Record<string, unknown>,
): Promise<PagedResult<T>> {
  const envelope = await request<T[]>({ method: 'GET', url, params });
  return {
    items: envelope.data,
    total: envelope.meta?.total ?? null,
    cursor: envelope.meta?.cursor ?? null,
  };
}

export async function apiPost<T>(url: string, body?: unknown): Promise<T> {
  return (await request<T>({ method: 'POST', url, data: body })).data;
}

export async function apiPatch<T>(url: string, body?: unknown): Promise<T> {
  return (await request<T>({ method: 'PATCH', url, data: body })).data;
}

export async function apiPut<T>(url: string, body?: unknown): Promise<T> {
  return (await request<T>({ method: 'PUT', url, data: body })).data;
}

export async function apiDelete(url: string, params?: Record<string, unknown>): Promise<void> {
  await request<null>({ method: 'DELETE', url, params });
}

/** Raw bytes — bundle downloads and export archives are files, not envelopes (spec §7.8). */
export async function apiDownload(url: string, params?: Record<string, unknown>): Promise<Blob> {
  try {
    const response = await client().request<Blob>({
      method: 'GET',
      url,
      params,
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    throw toClientError(error);
  }
}

/** Test seam: lets a test install a stub instance without going through MSAL. */
export function __setApiClientForTests(next: AxiosInstance | null): void {
  instance = next;
}
